<template>
    <section class="log-in flow__panel">
        <h2 class="log-in__title">
            Log In
        </h2>
        <app-sponsorship class="log-in__sponsorship" />
        <p>
            In order to proceed we need you to log in to your Plan Builder account.
            <span v-if="showRegisterLink">If you are new to Plan builder you can
                <router-link :to="{ name: 'Register', params: { redirect: this.redirect } }"
                             class="log-in__register-link">register here</router-link>.
            </span>
        </p>
        <hh-msg-display class="log-in__msg" :message="message" :type="messageType" />
        <hh-error-display class="log-in__error" :error="error" />
        <hh-form class="log-in__form">
            <hh-form-group>
                <hh-email ref="model.Email"
                          class="log-in__form__input log-in__form__input--email"
                          placeholder="Enter your email address"
                          :auto-fill="true"
                          v-model="email"
                          @change="resetValidation"
                          @keyup.enter="onLogInClickAsync" />
            </hh-form-group>
            <hh-form-group>
                <hh-validator for="model.Email" />
            </hh-form-group>
            <hh-form-group>
                <hh-password def="password"
                             ref="model.Password"
                             class="log-in__form__input log-in__form__input--password"
                             placeholder="Enter your password"
                             :auto-fill="true"
                             v-model="password"
                             @change="resetValidation"
                             @keyup.enter="onLogInClickAsync" />
            </hh-form-group>
            <hh-form-group>
                <hh-validator for="model.Password" />
            </hh-form-group>
            <div class="log-in__form__buttons">
                <hh-btn v-if="showCancelButton"
                        type="cancel"
                        class="log-in__form__button log-in__form__button--cancel"
                        icon-class="fas fa-undo"
                        icon-location="pre"
                        text="Cancel"
                        @click="onCancelClick" />
                <hh-btn v-if="showGuestButton"
                        class="log-in__form__button log-in__form__button--guest"
                        text="Try as a Visitor"
                        @click="onGuestClick" />
                <hh-btn class="log-in__form__button log-in__form__button--forgotten"
                        text="Forgot password"
                        @click="onForgottenPasswordClickAsync" />
                <hh-btn type="cta"
                        class="log-in__form__button log-in__form__button--create"
                        icon-class="fas fa-sign-in-alt"
                        icon-location="pre"
                        text="Log In"
                        :track-operation-state="true"
                        :operation-in-progress="loggingIn"
                        @click="onLogInClickAsync" />
            </div>
            <hh-form-group v-if="showRecaptcha">
                <hh-recaptcha-invisible ref="invisibleRecaptcha"
                                        class="log-in__form__catch"
                                        @verified="onVerified"
                                        @already-verified="onAlreadyVerified" />
            </hh-form-group>
        </hh-form>
    </section>
</template>

<script>

import Vuex from 'vuex'

import AuthManager from '@/auth/auth-manager'

import AnalyticsPage from './analytics-page'

import AppSponsorship from '../components/app-core/app-sponsorship'

/**
 * When the ReCaptcha flow is complete, execute the login code.
 * @type {number}
 */
const RECAPTCHA_FLOW_LOGIN = 1

/**
 * When the ReCaptcha flow is complete, execute the forgotten password code.
 * @type {number}
 */
const RECAPTCHA_FLOW_FORGOTTEN_PASSWORD = 2

export default {

    props: {
        redirect: {
            type: Object,
            default: null,
        },
    },

    mixins: [
        AnalyticsPage,
    ],

    computed: {
        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {?string}
             */
            requestToken: stateStore => stateStore.requestToken,
        }),

        ...Vuex.mapGetters([
            'selectedProperty',
            'lastGuestAccessibleBackIncludedRoute',
        ]),

        /**
         * @returns {boolean}
         */
        showCancelButton() {
            return !this.userKey
        },

        /**
         * @returns {?string}
         */
        showGuestButton() {
            return this.userKey
        },

        /**
         * @returns {boolean}
         */
        showRegisterLink() {
            return this.selectedProperty != null
        },

        /**
         * @returns {?string}
         */
        userKey() {
            return this.$route.params.key || null
        },

        /**
         * @returns {boolean}
         */
        canProceed() {
            return this.requestToken && !this.requestToken.isExpired || this.captchaVerified
        },
    },

    data() {
        return {
            /**
             * @type {?string}
             */
            password: null,

            /**
             * @type {?string}
             */
            email: null,

            /**
             * @type {?string}
             */
            message: null,

            /**
             * @type {string}
             */
            messageType: 'info',

            /**
             * @type {?string}
             */
            error: null,

            /**
             * @type {boolean}
             */
            loggingIn: false,

            /**
             * @type {boolean}
             */
            allowValidationReset: true,

            /**
             * @type {boolean}
             */
            showRecaptcha: true,

            /**
             * @type {boolean}
             */
            captchaVerified: false,

            /**
             * @type {number}
             */
            recaptchaFlow: 0,
        }
    },

    beforeMount() {
        if (this.userKey && this.userKey.includes('@'))
            this.email = this.userKey.replace('Household-', '').toLowerCase()
    },

    /**
   * View mounted event.
   */
    mounted() {
        this.$logger.logObject(this.redirect, 'Log In requested', 'Log In')

        if (this.requestToken !== null)
            this.showRecaptcha = this.requestToken.isExpired
    },

    methods: {

        /**
         * Gets the input element node from the DOM.
         * @param {string} refId
         * @returns {HTMLInputElement}
         * @private
         */
        _getRefInputNode(refId) {
            // noinspection JSValidateTypes
            return this.$refs[refId].$el.childNodes[0]
        },

        /**
         * Handles autofilled entries from annoying browser helpers.
         * @private
         */
        _handleAutofilledEntries() {
            this.email = this._getRefInputNode('model.Email').value
            this.password = this._getRefInputNode('model.Password').value
            this.$logger.logObject({ email: this.email, passwordSet: this.password !== null }, 'Autofill values captured', 'Log In')
        },

        /**
         * Redirects the app.
         * @private
         */
        _redirect() {
            if (this.redirect) {
                this.$logger.logObject(this.redirect, 'Redirecting, post login', 'Routing')
                this.$router.replace(this.redirect.fullPath)
            } else {
                this.$router.push({ name: 'Dashboard' })
            }
        },

        /**
         * Resets the passwords to empty.
         * @private
         */
        _resetPasswords() {
            this.password = null
            this._getRefInputNode('model.Password').value = ''
        },

        /**
         * Event Method.
         */
        onCancelClick() {
            this.$router.push(this.lastGuestAccessibleBackIncludedRoute)
        },

        /**
         * Event Method.
         */
        onGuestClick() {
            this.$router.push({ name: 'Welcome' })
        },

        /**
         * Event Method.
         * @returns {Promise<void>}
         */
        async onForgottenPasswordClickAsync() {
            if (this.canProceed) {
                await this._executeForgottenPassword()
            } else if (this.$refs.invisibleRecaptcha && this.$refs.invisibleRecaptcha.execute) {
                this.recaptchaFlow = RECAPTCHA_FLOW_FORGOTTEN_PASSWORD
                this.$refs.invisibleRecaptcha.execute()
            }
        },

        /**
         * Send a forgotten password email.
         * @returns {Promise<void>}
         */
        async _executeForgottenPassword() {
            this.allowValidationReset = true
            this.resetValidation()
            this.allowValidationReset = false

            try {
                await AuthManager.forgottenPasswordAsync(this.email)
                this.message = 'A forgotten password request has been sent. You should receive an email shortly. If it doesn\'t arrive in your Inbox make sure to check your junk e-mail & spam folders.'
                this.allowValidationReset = true
            } catch (err) {
                this._resetPasswords()
                const validation = this.$serverValidator.bindResults(err, this.$refs)
                this.error = this.$serverValidator.formatErrorMessageFromResults(validation, 'Forgotten Password request failed')
            }
        },

        /**
         * Event Method.
         * @returns {Promise<void>}
         */
        async onLogInClickAsync() {
            if (this.canProceed) {
                await this._executeLogin()
            } else if (this.$refs.invisibleRecaptcha && this.$refs.invisibleRecaptcha.execute) {
                this.recaptchaFlow = RECAPTCHA_FLOW_LOGIN
                this.$refs.invisibleRecaptcha.execute()
            }
        },

        async _executeLogin() {
            this.allowValidationReset = true
            this.resetValidation()
            this.allowValidationReset = false

            try {
                this._handleAutofilledEntries()
                this.loggingIn = true
                await AuthManager.logInAsync(this.email, this.password)
                this._redirect()
                this.loggingIn = false
                this.allowValidationReset = true
            } catch (err) {
                this._resetPasswords()
                const validation = this.$serverValidator.bindResults(err, this.$refs)
                this.error = this.$serverValidator.formatErrorMessageFromResults(validation, 'Log in failed')
                this.loggingIn = false
            }
        },

        /**
         * Resets the validation tracker and screen.
         */
        resetValidation() {
            if (!this.allowValidationReset) return

            this.$serverValidator.clear(this.$refs)
            this.error = null
            this.message = null
        },

        /**
         * Event handler for recaptcha component. Called if recaptcha has already been verified.
         * @param {boolean} verified
         */
        onAlreadyVerified(verified) {
            this.showRecaptcha = !verified
            this.captchaVerified = verified
        },

        /**
         * Event handler for recaptcha component. Called when recaptcha has completed verification.
         * @async
         * @param {boolean} [isVerified = true]
         */
        async onVerified(isVerified = true) {
            this.captchaVerified = isVerified

            if (isVerified) {
                if (this.recaptchaFlow === RECAPTCHA_FLOW_LOGIN)
                    await this._executeLogin()
                else if (this.recaptchaFlow === RECAPTCHA_FLOW_FORGOTTEN_PASSWORD)
                    await this._executeForgottenPassword()
            }
        },
    },

    components: {
        AppSponsorship,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_log-in.scss

.log-in {
    min-height: 0;

    @include bp-min-mid {
        max-width: 68%;
    }

    @include bp-min-wide {
        max-width: 54%;
    }

    @include bp-min-wider {
        max-width: 40%;
    }

    @include element('register-link') {
        font-weight: bold;
    }

    @include element('form') {

        @include element('input') {
            width: 100%;
        }

        @include element('buttons') {
            padding-bottom: 5px;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            @include bp-min-small {
                flex-direction: row;
            }
        }

        @include element('button') {
            margin-bottom: 0.5em !important;
            width: 100%;
            min-width: rem(100px);

            @include bp-min-small {
                width: auto;
                margin-bottom: 0 !important;
            }

            @include modifier('ok') {
                float: right;

                @include bp-min-small {
                    margin-top: 80px;
                }
            }
        }
    }
}

</style>
